
import React, { createContext, useContext, useState, useEffect } from 'react';

export interface Widget {
  id: string;
  title: string;
  isVisible: boolean;
  order: number;
}

interface DashboardLayoutContextType {
  widgets: Widget[];
  toggleWidgetVisibility: (widgetId: string) => void;
  reorderWidgets: (activeId: string, overId: string) => void;
  saveLayout: () => Promise<void>;
  resetLayout: () => void;
  isCustomizing: boolean;
  setIsCustomizing: (customizing: boolean) => void;
}

const defaultWidgets: Widget[] = [
  { id: 'analytics-section', title: 'التحليلات والإحصائيات', isVisible: true, order: 1 },
  { id: 'client-status-cards', title: 'حالات العملاء', isVisible: true, order: 2 },
  { id: 'client-status-chart', title: 'توزيع حالات العملاء', isVisible: true, order: 3 },
  { id: 'top-employees', title: 'ترتيب أفضل الموظفين', isVisible: true, order: 4 },
  { id: 'package-performance', title: 'أداء الباقات السياحية', isVisible: true, order: 5 },
  { id: 'package-kpis', title: 'مؤشرات الأداء الرئيسية للباقات', isVisible: true, order: 6 },
  { id: 'package-financials', title: 'التحليلات المالية للباقات', isVisible: true, order: 7 },
  { id: 'employee-package-performance', title: 'أداء الموظفين في مبيعات الباقات', isVisible: true, order: 8 }
];

const DashboardLayoutContext = createContext<DashboardLayoutContextType | undefined>(undefined);

export const DashboardLayoutProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [widgets, setWidgets] = useState<Widget[]>(defaultWidgets);
  const [isCustomizing, setIsCustomizing] = useState(false);

  useEffect(() => {
    const savedLayout = localStorage.getItem('dashboard-layout');
    if (savedLayout) {
      try {
        const parsedLayout = JSON.parse(savedLayout);
        setWidgets(parsedLayout);
      } catch (error) {
        console.error('Error parsing saved layout:', error);
        // Reset to default on parse error
        setWidgets(defaultWidgets);
      }
    }
  }, []);

  const toggleWidgetVisibility = (widgetId: string) => {
    setWidgets(prev => prev.map(widget => 
      widget.id === widgetId 
        ? { ...widget, isVisible: !widget.isVisible }
        : widget
    ));
  };

  const reorderWidgets = (activeId: string, overId: string) => {
    const activeIndex = widgets.findIndex(w => w.id === activeId);
    const overIndex = widgets.findIndex(w => w.id === overId);
    
    if (activeIndex === -1 || overIndex === -1) return;

    const newWidgets = [...widgets];
    const [removed] = newWidgets.splice(activeIndex, 1);
    newWidgets.splice(overIndex, 0, removed);
    
    // تحديث الترتيب
    const updatedWidgets = newWidgets.map((widget, index) => ({
      ...widget,
      order: index + 1
    }));
    
    setWidgets(updatedWidgets);
  };

  const saveLayout = async () => {
    localStorage.setItem('dashboard-layout', JSON.stringify(widgets));
  };

  const resetLayout = () => {
    setWidgets(defaultWidgets);
    localStorage.removeItem('dashboard-layout');
  };

  return (
    <DashboardLayoutContext.Provider value={{
      widgets,
      toggleWidgetVisibility,
      reorderWidgets,
      saveLayout,
      resetLayout,
      isCustomizing,
      setIsCustomizing
    }}>
      {children}
    </DashboardLayoutContext.Provider>
  );
};

export const useDashboardLayout = () => {
  const context = useContext(DashboardLayoutContext);
  if (context === undefined) {
    throw new Error('useDashboardLayout must be used within a DashboardLayoutProvider');
  }
  return context;
};
